import { fabric } from 'fabric';
import uniqueId from 'lodash/uniqueId';
import { useCallback } from 'react';
import Vec2 from 'vec2';

import { useActiveAppliance } from '~/core/hooks/useActiveAppliance';
import { ApplianceModel, DrawingAppliance, DrawingShape } from '~/core/models';
import {
  isShapeValidForApplianceInside,
  isShapeValidForApplianceOutside,
} from '~/core/utils';
import {
  ensureMutableArray,
  forceAssignProperty,
} from '~/core/utils/objectMutability.utils';
import { useFabricCanvasContext } from '~/legacy/easy-estimator-page/context';

export const useDropHandler = (canvas: fabric.Canvas) => {
  const { drawingShapesRef } = useFabricCanvasContext();

  const { activateAddedAppliance } = useActiveAppliance();

  const findApplianceAndShape = useCallback(
    (
      e: DragEvent
    ):
      | {
          appliance: ApplianceModel;
          shape: DrawingShape;
          targetShape?: DrawingShape;
        }
      | undefined => {
      const appliance: ApplianceModel | null = JSON.parse(
        e?.dataTransfer?.getData('text') || 'null'
      );

      if (!appliance) {
        return undefined;
      }

      const centerPoint = new Vec2(canvas.getPointer(e));
      const [targetShape, fauxShape] = [
        isShapeValidForApplianceInside(appliance.applianceType, centerPoint),
        isShapeValidForApplianceOutside(appliance.applianceType),
      ].map((predicate) => drawingShapesRef.current?.find(predicate));
      const shape = targetShape ?? fauxShape;

      if (!shape) {
        return undefined;
      }

      return { appliance, shape, targetShape };
    },
    [drawingShapesRef, canvas]
  );

  const addApplianceToShape = useCallback(
    (args: {
      appliance: ApplianceModel;
      shape: DrawingShape;
      centerPoint: Vec2;
      targetShape: DrawingShape | undefined;
      hasChipMinimizer?: boolean;
    }) => {
      const { appliance, shape, centerPoint, targetShape, hasChipMinimizer } =
        args;
      const newApplianceId = uniqueId();
      const newDraggingAppliance: DrawingAppliance = {
        id: newApplianceId,
        applianceModel: appliance,
        centerPoint: centerPoint,
        angle: 0,
        height: targetShape ? appliance.width : 0,
        width: targetShape ? appliance.length : 0,
        hasChipMinimizer,
      };

      const mutableAppliancesArray = ensureMutableArray(shape.appliances);

      if (mutableAppliancesArray !== shape.appliances) {
        forceAssignProperty(shape, 'appliances', mutableAppliancesArray);
      }

      mutableAppliancesArray.push(newDraggingAppliance);

      activateAddedAppliance(newDraggingAppliance);
    },
    [activateAddedAppliance]
  );

  return {
    addApplianceToShape,
    findApplianceAndShape,
  };
};
